import {
  TypePurchaseMetadata,
  TypeSubscriberShopProduct,
  TypeVariant,
  TypeKitMetaData,
} from '@/lib/api/productCatalog/types';
import { ShopifyVariant } from '@/lib/shopify';
import { TypeSubscriberShopPageTranslations } from '@/utils/translations';
import moment from 'moment';

export const DEFAULT_PRODUCT_CARDS_COUNT = 6;

export const DIVIDER_IMAGES: { [key: number]: string } = {
  0: '/assets/images/divider-green.png',
  1: '/assets/images/divider-pink.png',
  2: '/assets/images/divider-purple.png',
  3: '/assets/images/divider-teal.png',
  4: '/assets/images/divider-yellow.png',
};

export const findSequentialKitsOverSixMonths = (
  products: TypeSubscriberShopProduct[],
): {
  sequential: boolean;
  kits: TypeSubscriberShopProduct[];
} => {
  const result = {
    sequential: false,
    kits: [] as TypeSubscriberShopProduct[],
  };

  if (products.length < 2) {
    return result;
  }

  const kitProducts = products.filter(
    (product) =>
      product.kit_metadata &&
      typeof product.kit_metadata.kit_position === 'number' &&
      typeof product.kit_metadata.kit_months_span === 'number',
  );

  if (kitProducts.length < 2) {
    return result;
  }

  kitProducts.sort(
    (a, b) => a.kit_metadata.kit_position - b.kit_metadata.kit_position,
  );

  const playKitsMonthsLimit = 6;
  let totalMonthsSpan = 0;
  let startIndex = 0;
  let sequentialCount = 1;

  for (let i = 0; i < kitProducts.length; i++) {
    const currentProduct = kitProducts[i];
    const prevProduct = kitProducts[i - 1];

    if (i === 0) {
      totalMonthsSpan += currentProduct.kit_metadata.kit_months_span;
      continue;
    }

    if (
      currentProduct.kit_metadata.kit_position !==
        prevProduct.kit_metadata.kit_position + 1 ||
      totalMonthsSpan >= playKitsMonthsLimit
    ) {
      startIndex = i; // Reset the start index if not sequential or months limit reached
      totalMonthsSpan = currentProduct.kit_metadata.kit_months_span;
      sequentialCount = 1; // Reset sequential count
    } else {
      totalMonthsSpan += currentProduct.kit_metadata.kit_months_span;
      sequentialCount++;
    }

    if (totalMonthsSpan >= playKitsMonthsLimit) {
      return {
        sequential: true,
        kits: kitProducts.slice(startIndex, startIndex + sequentialCount),
      };
    }
  }

  return result;
};

export function getTranslationForRule(
  rule: string,
  translations: TypeSubscriberShopPageTranslations,
): string | undefined {
  const allowedRules: { [key: string]: string } = {
    'refreshed-in': translations.ruleUpdated,
    refill: translations.ruleRefill,
  };

  return allowedRules[rule];
}

export function isOrderFrequencyFreezePeriodActive(
  metadata: TypePurchaseMetadata,
  todayDateString: string,
) {
  const { order_frequency, last_purchased } = metadata;
  if (!order_frequency || !last_purchased) return false;

  const lastDayOfFreezePeriod = moment(last_purchased).add(
    metadata.order_frequency,
    'days',
  );

  return moment(todayDateString).isSameOrBefore(lastDayOfFreezePeriod, 'days');
}

export function getShopifyProductVariant(
  kitMetadata: TypeKitMetaData,
  variants: TypeVariant[],
  shopifyVariants: ShopifyVariant[],
): ShopifyVariant {
  if (shopifyVariants.length === 1 || !kitMetadata.variant_uuid) {
    return shopifyVariants[0];
  }

  const recommendedVariantUuid = kitMetadata.variant_uuid;

  if (!recommendedVariantUuid) {
    return shopifyVariants[0] as ShopifyVariant;
  } else {
    const productVariantObj = variants.find(
      (variant: TypeVariant) => variant.uuid === recommendedVariantUuid,
    );

    if (productVariantObj) {
      const index = shopifyVariants.findIndex(
        (variant: ShopifyVariant) => variant.sku === productVariantObj.sku,
      );

      if (index !== -1) {
        return shopifyVariants[index];
      }
    }
  }

  return shopifyVariants[0];
}
